import { Button, Row } from '@rematter/ui-web';
import { LucideRotateCcw } from 'lucide-react';

/**
 * Single digit box for the access code display
 */
type CodeDigitProps = { children: React.ReactNode };

const CodeDigit = ({ children }: CodeDigitProps) => (
  <div className="@CodeDigit flex h-10 w-10 items-center justify-center rounded-md bg-neutral/7 text-base text-neutral/60">
    {children}
  </div>
);

export type AccessCodeDisplayProps = {
  code: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

/**
 * Displays a 6-digit access code in 3-3 format with a refresh button
 */
export const AccessCodeDisplay = ({
  code,
  onRefresh,
  isRefreshing,
}: AccessCodeDisplayProps) => {
  // Ensure code is 6 digits, pad with zeros if needed
  const normalizedCode = code.padStart(6, '0').slice(0, 6);
  const digits = normalizedCode.split('');

  return (
    <Row className="@AccessCodeDisplay items-center gap-1.5">
      {/* First 3 digits */}
      <CodeDigit>{digits[0]}</CodeDigit>
      <CodeDigit>{digits[1]}</CodeDigit>
      <CodeDigit>{digits[2]}</CodeDigit>

      {/* Separator */}
      <span className="px-0.5 text-base text-neutral/40">–</span>

      {/* Last 3 digits */}
      <CodeDigit>{digits[3]}</CodeDigit>
      <CodeDigit>{digits[4]}</CodeDigit>
      <CodeDigit>{digits[5]}</CodeDigit>

      {/* Refresh button */}
      <Button
        variant="outline"
        size="icon"
        icon={<LucideRotateCcw className="size-4" />}
        onPress={onRefresh}
        isDisabled={isRefreshing}
        className="ml-1"
        aria-label="Regenerate access code"
      />
    </Row>
  );
};
